import { ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularLoader,
  LinkButton,
  Typography,
} from "@bca/ui-library";

import { Vehicle } from "../Interfaces/vehicle.type";
import Price from "./Price";
import Transport from "./Transport";
import CTAs from "./CTAs";
import getSectionType from "./getSectionType";
import contactPartnerFinanceDataPush from "../AdobeDataPushes/vehicleCard/contactPartnerFinanceDataPush";
import collectionsDataPush from "../AdobeDataPushes/vehicleCard/collectionsDataPush";
import deliveriesDataPush from "../AdobeDataPushes/vehicleCard/deliveriesDataPush";
import { paymentStatusMapper } from "../Mappers/vehicleDetailsMappers";
import { useGetRequestDeliveryLink } from "../Hooks/useGetRequestDeliveryLink";
import { storeType } from "../redux/store";
import selectForMultipleDelivery from "../Utilities/selectForMultipleDelivery";
import {
  DEFAULT_BLUE_BACKGROUND,
  GREEN_BACKGROUND,
  RED_BACKGROUND,
  YELLOW_BACKGROUND,
} from "./CardButtonSectionBackgrounds";
import requestMultipleDeliveriesLinkDataPush from "../AdobeDataPushes/vehicleCard/requestMultipleDeliveriesLinkDataPush";
import selectForMultipleDeliveryDataPush from "../AdobeDataPushes/vehicleCard/selectForMultipleDeliveryDataPush";

interface PriceTransportCTAsContainerProps {
  vehicle: Vehicle;
  isChecked: boolean;
  isDisabled: boolean;
  degradedServices: string[];
  openTransportModal: () => void;
  openDeliveryModal: () => void;
  openInvoiceModal: () => void;
  invoiceModalLoading: boolean;
}

const PriceTransportCTAsContainer: React.FunctionComponent<
  PriceTransportCTAsContainerProps
> = ({
  vehicle,
  isChecked,
  isDisabled,
  degradedServices = [],
  openTransportModal,
  openInvoiceModal,
  invoiceModalLoading,
}) => {
  const store = useSelector((state: storeType) => ({
    selectedForDelivery: state.bookDelivery.selectedForDelivery,
  }));
  const dispatch = useDispatch();
  const selectForMultipleDeliveryHandler = () => {
    selectForMultipleDeliveryDataPush(vehicle, "button", "select", "card");
    selectForMultipleDelivery(vehicle, dispatch);
  };

  const requestDeliveryLink = useGetRequestDeliveryLink();

  const deliveriesAnalyticsPush = () =>
    deliveriesDataPush(vehicle, vehicle.isExport);
  const requestMultipleDeliveriesAnalyticsPush = () =>
    requestMultipleDeliveriesLinkDataPush("card");
  const collectionsAnalyticsDataPush = () => collectionsDataPush(vehicle);
  const contactPartnerFinanceAnalyticsDataPush = () =>
    contactPartnerFinanceDataPush(vehicle);

  const sectionType = getSectionType(vehicle, degradedServices);
  interface ctas {
    backgroundColor: string;
    firstCTA: ReactElement;
    secondCTA: ReactElement;
  }
  const handleMultipleDeliveryButtons = (
    isChecked: boolean,
    isDisabled: boolean,
    defaultButtons: ctas,
    requestDeliveryButton: ctas,
    selectForDeliveryButton: ctas
  ) => {
    if (isChecked) {
      return requestDeliveryButton;
    } else if (
      !isChecked &&
      !isDisabled &&
      Boolean(store.selectedForDelivery.length)
    ) {
      return selectForDeliveryButton;
    } else {
      return defaultButtons;
    }
  };

  const deliveriesLink = (marginLeft: number) => (
    <LinkButton
      fontSize="body-small"
      variant="secondary"
      link={`${process.env.VIRTUAL_DOMAIN}mybca/deliveries`}
      onClick={deliveriesAnalyticsPush}
      height="42px"
      width="50%"
      marginLeft={marginLeft}
      px={2}
    >
      Your delivery
    </LinkButton>
  );
  const requestDeliveryLinkButton = (
    <LinkButton
      fontSize="body-small"
      variant="primary"
      link={requestDeliveryLink}
      onClick={requestMultipleDeliveriesAnalyticsPush}
      height="42px"
      width="100%"
      px={2}
      shouldOpenInNewWindow={false}
    >
      Request Delivery
    </LinkButton>
  );
  const selectForDeliveryButton = (
    <Button
      fontSize="body-small"
      variant="secondary"
      onClick={selectForMultipleDeliveryHandler}
      height="42px"
      width="100%"
      px={2}
    >
      Select for Delivery
    </Button>
  );
  const collectionsLink = (
    <LinkButton
      fontSize="body-small"
      variant="secondary"
      onClick={collectionsAnalyticsDataPush}
      link={`${process.env.VIRTUAL_DOMAIN}mybca/collections/`}
      height="42px"
      width="50%"
      px={2}
    >
      Your collection
    </LinkButton>
  );
  const bookTransportButton = (marginLeft: number) => (
    <Button
      fontSize="body-small"
      variant="primary"
      onClick={openTransportModal}
      height="42px"
      width="50%"
      marginLeft={marginLeft}
      px={1}
    >
      Book transport
    </Button>
  );
  const payInvoiceButton = (marginRight: number = 0) => (
    <Button
      fontSize="body-small"
      variant="primary"
      onClick={openInvoiceModal}
      height="42px"
      width="50%"
      marginRight={marginRight}
      px={4}
    >
      {invoiceModalLoading ? (
        <CircularLoader
          title="LoadData"
          width="20px"
          height="20px"
          dotsColor="white"
        />
      ) : (
        "Pay Invoice"
      )}
    </Button>
  );
  const viewInvoiceButton = (marginRight: number = 0) => (
    <Button
      fontSize="body-small"
      variant="secondary"
      onClick={openInvoiceModal}
      height="42px"
      width="50%"
      marginRight={marginRight}
      px={4}
    >
      {invoiceModalLoading ? (
        <CircularLoader title="LoadData" width="30px" height="30px" />
      ) : (
        "View Invoice"
      )}
    </Button>
  );
  const seeEmailsForInvoiceText = (
    <Box width="50%" display="flex" alignItems="center">
      <Typography
        variant="body-small"
        color="secondary-orange"
        fontWeight="medium"
        marginY={0}
      >
        See emails for invoice
      </Typography>
    </Box>
  );
  const invoiceGeneratingText = (
    <Typography
      variant="body-small"
      color="secondary-orange"
      fontWeight="medium"
      marginY={3}
    >
      Invoice Generating
    </Typography>
  );
  const partnerFinancedText = (
    <Typography
      variant="body-small"
      color="secondary-green"
      fontWeight="medium"
      marginY={3}
    >
      Partner Financed
    </Typography>
  );
  const contactPartnerFinanceButton = (
    <LinkButton
      fontSize="body-small"
      variant="secondary"
      link={`${process.env.VIRTUAL_DOMAIN}services/finance-your-forecourt`}
      onClick={contactPartnerFinanceAnalyticsDataPush}
      height="42px"
      width="100%"
      px={4}
    >
      Contact Partner Finance
    </LinkButton>
  );

  const sectionSettings = {
    exported: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: seeEmailsForInvoiceText,
      secondCTA: deliveriesLink(5),
    },
    exportedDeliveriesDegraded: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: seeEmailsForInvoiceText,
      secondCTA: null,
    },
    export: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: seeEmailsForInvoiceText,
      secondCTA: deliveriesLink(5),
    },
    exportDeliveriesDegraded: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: seeEmailsForInvoiceText,
      secondCTA: null,
    },
    invoiceReadyNoTransportBooked: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: payInvoiceButton(5),
        secondCTA: bookTransportButton(0),
      },
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    invoiceReadyNoTransportBookedNotEligibleForDelivery: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: payInvoiceButton(5),
      secondCTA: bookTransportButton(0),
    },
    invoiceReadyDeliveryCancelled: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: payInvoiceButton(5),
        secondCTA: bookTransportButton(0),
      },
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: DEFAULT_BLUE_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    invoiceReadyDeliveryBooked: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: payInvoiceButton(5),
      secondCTA: deliveriesLink(0),
    },
    invoiceReadyDeliveryBookedDeliveriesDegraded: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: payInvoiceButton(5),
      secondCTA: null,
    },
    paymentFailedNoTransportBooked: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: payInvoiceButton(5),
        secondCTA: bookTransportButton(0),
      },
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    paymentFailedNoTransportBookedNotEligibleForDelivery: {
      backgroundColor: RED_BACKGROUND,
      firstCTA: payInvoiceButton(5),
      secondCTA: bookTransportButton(0),
    },
    paymentFailedDeliveryCancelled: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: payInvoiceButton(5),
        secondCTA: bookTransportButton(0),
      },
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: RED_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    paymentFailedDeliveryBooked: {
      backgroundColor: RED_BACKGROUND,
      firstCTA: payInvoiceButton(5),
      secondCTA: deliveriesLink(0),
    },
    paymentFailedDeliveryBookedDeliveriesDegraded: {
      backgroundColor: RED_BACKGROUND,
      firstCTA: null,
      secondCTA: payInvoiceButton(),
    },
    paymentProcessingNoTransportBooked: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: YELLOW_BACKGROUND,
        firstCTA: viewInvoiceButton(),
        secondCTA: bookTransportButton(5),
      },
      {
        backgroundColor: YELLOW_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: YELLOW_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    paymentProcessingDeliveryBooked: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: viewInvoiceButton(),
      secondCTA: deliveriesLink(5),
    },
    paymentProcessingDeliveryBookedDeliveriesDegraded: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: viewInvoiceButton(),
      secondCTA: null,
    },
    invoicePaidNoTransportBooked: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: viewInvoiceButton(),
        secondCTA: bookTransportButton(5),
      },
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    invoicePaidNoTransportBookedIsOffsiteTrue: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: viewInvoiceButton(5),
      secondCTA: null,
    },
    invoicePaidCollectionBooked: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: viewInvoiceButton(5),
      secondCTA: collectionsLink,
    },
    invoicePaidCollectionBookedCollectionsDegraded: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: viewInvoiceButton(5),
      secondCTA: null,
    },
    invoicePaidDeliveryBooked: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: viewInvoiceButton(),
      secondCTA: deliveriesLink(5),
    },
    invoicePaidDeliveryBookedDeliveriesDegraded: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: viewInvoiceButton(),
      secondCTA: null,
    },
    invoiceGeneratingDeliveryBooked: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: <Box width="50%">{invoiceGeneratingText}</Box>,
      secondCTA: deliveriesLink(5),
    },
    invoiceGeneratingDeliveryBookedDeliveriesDegraded: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: (
        <Box width="100%" display="flex" justifyContent="center">
          {invoiceGeneratingText}
        </Box>
      ),
      secondCTA: null,
    },
    invoiceGeneratingNoTransportBooked: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: (
        <Box width="100%" display="flex" justifyContent="center">
          {invoiceGeneratingText}
        </Box>
      ),
      secondCTA: null,
    },
    partnerFinanceInvoiceNoTransportBooked: handleMultipleDeliveryButtons(
      isChecked,
      isDisabled,
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: partnerFinancedText,
        secondCTA: bookTransportButton(5),
      },
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: requestDeliveryLinkButton,
        secondCTA: null,
      },
      {
        backgroundColor: GREEN_BACKGROUND,
        firstCTA: selectForDeliveryButton,
        secondCTA: null,
      }
    ),
    partnerFinanceInvoiceNoTransportBookedIsOffsiteTrue: {
      backgroundColor: GREEN_BACKGROUND,
      firstCTA: contactPartnerFinanceButton,
      secondCTA: null,
    },
    PartnerFinanceProcessing: {
      backgroundColor: YELLOW_BACKGROUND,
      firstCTA: contactPartnerFinanceButton,
      secondCTA: null,
    },
    PartnerFinanceFailed: {
      backgroundColor: RED_BACKGROUND,
      firstCTA: contactPartnerFinanceButton,
      secondCTA: null,
    },
    noInvoice: {
      backgroundColor: DEFAULT_BLUE_BACKGROUND,
      firstCTA: null,
      secondCTA: null,
    },
  };

  const getBottomSectionBackground = (deliveryStatus: string) => {
    let backgroundColor = "";
    deliveryStatus === "Delivery Cancelled"
      ? (backgroundColor = paymentStatusMapper.Failed.backgroundColor)
      : (backgroundColor = sectionSettings[sectionType].backgroundColor);
    return backgroundColor;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      flexGrow={1}
      justifyContent="space-between"
      padding={4}
      paddingY={5}
      backgroundColor={getBottomSectionBackground(vehicle.orderState?.status)}
    >
      <Box display="flex" padding="0">
        <Price vehicle={vehicle} />
        <Transport vehicle={vehicle} degradedServices={degradedServices} />
      </Box>
      <CTAs
        first={sectionSettings[sectionType]?.firstCTA}
        second={sectionSettings[sectionType]?.secondCTA}
      />
    </Box>
  );
};

export default PriceTransportCTAsContainer;
